import React from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Select,
  TextField,
  Tooltip
} from '@mui/material';
import { V4_DIALOG_CONTENT, V4_ICON_BUTTON_SMALL } from '../../theme/v4Compat';
import type { UserDraft } from '../CreateUpdateUserModal/component';
import { extractApiErrorMessage, notifications, validatePasswordPolicy } from '../../utils';
import { useTranslation } from 'react-i18next';
import { ApiService } from '../../hooks';
import { AutocompleteInput, CultureSelect, FarmUserCard, PasswordTextField } from '../../elements';
import { CreateUpdateUserModal } from '../../components';
import PasswordPolicyChecklist from '../PasswordPolicyChecklist';
import useAuthContext from '../../hooks/useAuthContext';
import {
  HealthSystemDomainUsersResultUserViewModel,
  HealthSystemDomainFarmsCommandsCreateFarmCommand,
  HealthSystemDomainFarmsCommandsUpdateFarmCommand,
  HealthSystemDomainFarmsResultFarmViewModel,
  HealthSystemDALEnumsFarmType,
  HealthSystemDomainUsersCommandsCreateUserCommand,
  HealthSystemDALEnumsTemperatureUnit,
  HealthSystemDALEnumsWeightType
} from '../../api/HealthSystemApi';
import { getTemperatureUnitOptions } from '../../utils/temperatureUnitHelper';
import { getWeightUnitOptions } from '../../utils/weightUnitHelper';
import useFeatures, { FeatureType } from '../../hooks/features';
import Autocomplete from '@mui/material/Autocomplete';
import { formatTimezoneLabel } from '../../utils/farmDateTime';

// Mirrors the backend Coordinates value object and the farm command validators.
const MIN_LATITUDE = -90;
const MAX_LATITUDE = 90;
const MIN_LONGITUDE = -180;
const MAX_LONGITUDE = 180;

// `Intl.supportedValuesOf` is part of ES2022 and not yet in this project's `lib` (ES2020), so the
// narrowest honest type is a small ambient shape instead of `any`. The try/catch still covers
// runtimes where the method is missing at all.
interface IntlWithSupportedValuesOf {
  supportedValuesOf(input: string): string[];
}

const IANA_TIMEZONES: string[] = (() => {
  try {
    return (Intl as unknown as IntlWithSupportedValuesOf).supportedValuesOf('timeZone');
  } catch {
    return [];
  }
})();

interface CreateUpdateFarmModalProps {
  opened: boolean;
  onModalAction: (result: boolean | null) => void;
  mode: 'create' | 'update';
  farm: HealthSystemDomainFarmsResultFarmViewModel | null;
}

// Invite mode for CreateUpdateUserModal: no id, nothing filled in yet.
const EMPTY_FARM_USER: UserDraft = {};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="flex items-center gap-2 mt-2 mb-1">
    <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-400 shrink-0">{children}</span>
    <div className="flex-1 border-t border-gray-200" />
  </div>
);

const CreateUpdateFarmModal: React.FC<CreateUpdateFarmModalProps> = ({ opened, onModalAction, mode, farm }) => {
  const apiService = new ApiService();
  const { t } = useTranslation();
  const { hasFeature } = useFeatures();
  const passwordPolicyEnabled = hasFeature(FeatureType.USE_PASSWORD_POLICY);
  const { state, updateAuthState } = useAuthContext();

  const [name, setName] = React.useState(farm?.name || '');
  const [number, setNumber] = React.useState(farm?.number || '');
  const [password, setPassword] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [address, setAddress] = React.useState(farm?.address || '');
  const [farmType, setFarmType] = React.useState<HealthSystemDALEnumsFarmType>(farm?.type ?? 0);
  const [latitude, setLatitude] = React.useState<number | null>(farm?.latitude ?? null);
  const [longitude, setLongitude] = React.useState<number | null>(farm?.longitude ?? null);
  // Empty string means no error; the text doubles as the field's helper text.
  const [latitudeError, setLatitudeError] = React.useState('');
  const [longitudeError, setLongitudeError] = React.useState('');
  const [temperatureUnit, setTemperatureUnit] = React.useState<HealthSystemDALEnumsTemperatureUnit>(
    farm?.metadata?.temperatureUnit ?? 0
  );
  const [weightUnit, setWeightUnit] = React.useState<HealthSystemDALEnumsWeightType>(farm?.metadata?.weightUnit ?? 1);
  const [cultureName, setCultureName] = React.useState<string>(farm?.cultureName ?? 'en-NL');
  const [timezoneId, setTimezoneId] = React.useState<string>(farm?.timezoneId ?? '');

  const [isEditingTimezone, setIsEditingTimezone] = React.useState(false);

  const [users, setUsers] = React.useState<HealthSystemDomainUsersResultUserViewModel[]>([]);
  const [createUserDialogOpen, setCreateUserDialogOpen] = React.useState(false);
  const [isBusy, setIsBusy] = React.useState(false);

  React.useEffect(() => {
    setName(farm?.name || '');
    setNumber(farm?.number || '');
    setFarmType(farm?.type || 0);
    setAddress(farm?.address || '');
    setLatitude(farm?.latitude ?? null);
    setLongitude(farm?.longitude ?? null);
    setLatitudeError('');
    setLongitudeError('');
    setCultureName(farm?.cultureName ?? 'en-NL');
    setTemperatureUnit(farm?.metadata?.temperatureUnit ?? 0);
    setWeightUnit(farm?.metadata?.weightUnit ?? 1);
    setTimezoneId(farm?.timezoneId ?? '');
    setIsEditingTimezone(false);
  }, [farm]);

  const closeDialog = () => {
    clearFields();
    onModalAction(null);
  };

  const buildCreateFarmObject = (): HealthSystemDomainFarmsCommandsCreateFarmCommand => ({
    name,
    number,
    address: address || null,
    farmType,
    password,
    users: users.map(mapUserToCreateUserCommand),
    latitude,
    longitude,
    metadata: { temperatureUnit, weightUnit },
    cultureName,
    // The generated command type says `timezoneId: string`, but the create-farm endpoint accepts
    // null for "no timezone set yet" - the same shape latitude/longitude use. Cast is for this
    // one known-wrong field only, not the whole object.
    timezoneId: (timezoneId || null) as string
  });

  const mapUserToCreateUserCommand = (
    user: HealthSystemDomainUsersResultUserViewModel
  ): HealthSystemDomainUsersCommandsCreateUserCommand => ({
    email: user.email ?? '',
    name: user.name ?? '',
    role: user.role ?? '',
    languageIso: user.languageIso ?? 'en',
    farmId: user.farmId ?? null,
    availability: user.availability ?? [],
    stables: user.stables ?? []
  });

  const buildUpdateFarmObject = (): HealthSystemDomainFarmsCommandsUpdateFarmCommand => ({
    id: farm?.id || '',
    name: name || null,
    number: number ?? '',
    password: password || null,
    address: address || null,
    farmType,
    isSoftDeleted: farm?.isSoftDeleted ?? false,
    latitude,
    longitude,
    metadata: { temperatureUnit, weightUnit },
    cultureName
  });

  const updateTimezoneViaSettings = async (newTimezoneId: string): Promise<void> => {
    const settingsResult = await apiService.api.settings.settingsGetFarmSettings({});
    await apiService.api.settings.settingsUpdateFarmSettings({
      barChartData: settingsResult.data.dashboardBarChartSettings ?? [],
      contacts: settingsResult.data.contacts ?? [],
      options: settingsResult.data.options,
      timeZoneId: newTimezoneId || null
    });
  };

  const handleCreateFarm = () => {
    if (!validateForm()) return;
    setIsBusy(true);
    apiService.api.farms
      .farmsCreateFarm(buildCreateFarmObject())
      .then(() => {
        notifications.success(t('f-farm-add-ok'));
        clearFields();
        onModalAction(true);
      })
      .catch((e) => notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') })))
      .finally(() => setIsBusy(false));
  };

  const handleUpdateFarm = () => {
    if (!validateForm()) return;
    setIsBusy(true);
    const timezoneChanged = timezoneId !== (farm?.timezoneId ?? '');
    apiService.api.farms
      .farmsUpdateFarm(buildUpdateFarmObject())
      .then(async () => {
        if (timezoneChanged) {
          await updateTimezoneViaSettings(timezoneId);
        }
        notifications.success(t('f-farm-upd-ok'));
        if (state.farmId === farm?.id) {
          const updatedMetadata = { temperatureUnit, weightUnit };
          window.localStorage.setItem('farmMetadata', JSON.stringify(updatedMetadata));
          updateAuthState((prevState) => ({ ...prevState, farmMetadata: updatedMetadata }));
          if (timezoneChanged) {
            window.location.reload();
            return;
          }
        }
        clearFields();
        onModalAction(true);
      })
      .catch((e) => notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') })))
      .finally(() => setIsBusy(false));
  };

  const clearFields = () => {
    setName('');
    setPassword('');
    setNumber('');
    setFarmType(0);
    setAddress('');
    setLatitude(null);
    setLongitude(null);
    setLatitudeError('');
    setLongitudeError('');
    setTemperatureUnit(0);
    setWeightUnit(1);
    setTimezoneId('');
    setIsEditingTimezone(false);
    setUsers([]);
  };

  const handleSaveClick = () => {
    if (mode === 'create') handleCreateFarm();
    else handleUpdateFarm();
  };

  const validateForm = (): boolean => {
    if (!name) {
      notifications.warning(t('f-val-farm-name'));
      return false;
    }
    if (!number) {
      notifications.warning(t('f-val-farm-number'));
      return false;
    }
    if (mode === 'create' && !password) {
      notifications.warning(t('f-val-farm-pass'));
      return false;
    }
    if (passwordPolicyEnabled && password && !validatePasswordPolicy(password).isValid) {
      notifications.warning(t('f-val-usr-pass-policy'));
      setPasswordError(true);
      return false;
    }
    setPasswordError(false);
    if (!farmType) {
      notifications.warning(t('f-val-farm-type'));
      return false;
    }
    if (!validateCoordinates()) {
      return false;
    }
    if (temperatureUnit === null) {
      notifications.warning(t('f-val-farm-temperature-unit'));
      return false;
    }
    return true;
  };

  const isMissing = (value: number | null): boolean => value === null || Number.isNaN(value);

  const coordinateError = (value: number | null, min: number, max: number, rangeKey: string): string => {
    if (isMissing(value)) {
      // Half a pair cannot be applied, so the empty side has to be filled in.
      return t('f-val-farm-geo-coordinates-pair');
    }
    return (value as number) < min || (value as number) > max ? t(rangeKey) : '';
  };

  // Coordinates are the only input the weather provider accepts, so a new farm must carry them.
  // On edit they are optional: leaving both empty keeps the position already on record, so a name
  // or address can be fixed without looking a latitude up first - which also matters for farms the
  // coordinate backfill could not resolve. Ranges mirror the backend (-90..90 and -180..180).
  const validateCoordinates = (): boolean => {
    if (isMissing(latitude) && isMissing(longitude)) {
      if (mode === 'create') {
        const message = t('f-val-farm-geo-coordinates-required');
        setLatitudeError(message);
        setLongitudeError(message);
        notifications.warning(message);
        return false;
      }

      setLatitudeError('');
      setLongitudeError('');
      return true;
    }

    const nextLatitudeError = coordinateError(latitude, MIN_LATITUDE, MAX_LATITUDE, 'f-val-farm-latitude-range');
    const nextLongitudeError = coordinateError(longitude, MIN_LONGITUDE, MAX_LONGITUDE, 'f-val-farm-longitude-range');

    setLatitudeError(nextLatitudeError);
    setLongitudeError(nextLongitudeError);

    if (!nextLatitudeError && !nextLongitudeError) {
      return true;
    }

    notifications.warning(nextLatitudeError || nextLongitudeError);
    return false;
  };

  const handleRemoveUser = (index: number) => {
    users.splice(index, 1);
    setUsers([...users]);
  };

  const handleAddUser = (user: boolean | HealthSystemDomainUsersResultUserViewModel | null) => {
    setCreateUserDialogOpen(false);
    if (user !== null && typeof user !== 'boolean') {
      setUsers([...users, user]);
    }
  };

  const tzChanged = mode === 'update' && !!farm?.timezoneId && timezoneId !== farm.timezoneId;

  return (
    <>
      <Dialog open={opened} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{mode === 'create' ? t('f-farm-add-title') : t('f-farm-upd-title')}</DialogTitle>

        <DialogContent sx={V4_DIALOG_CONTENT}>
          <div className="flex flex-col gap-3 py-1 overflow-x-hidden">
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label={t('f-com-name')}
                size="small"
                fullWidth
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <TextField
                label={t('f-com-number')}
                size="small"
                fullWidth
                variant="outlined"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
              />
            </div>

            <PasswordTextField
              label={t('f-usr-pass')}
              size="small"
              fullWidth
              variant="outlined"
              value={password}
              error={passwordError}
              helperText={t('f-supp-farm-pass-info')}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError(false);
              }}
            />

            {passwordPolicyEnabled && password && <PasswordPolicyChecklist password={password} />}

            <TextField
              label={t('f-com-address')}
              size="small"
              fullWidth
              variant="outlined"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />

            <SectionLabel>Location</SectionLabel>

            <div className="grid grid-cols-2 gap-3">
              <TextField
                required={mode === 'create'}
                label={t('f-geo-latitude')}
                size="small"
                fullWidth
                variant="outlined"
                type="number"
                slotProps={{ htmlInput: { min: MIN_LATITUDE, max: MAX_LATITUDE, step: 'any' } }}
                value={latitude ?? ''}
                error={!!latitudeError}
                helperText={latitudeError}
                onChange={(e) => {
                  setLatitude(e.target.value === '' ? null : Number(e.target.value));
                  if (latitudeError) setLatitudeError('');
                }}
              />
              <TextField
                required={mode === 'create'}
                label={t('f-geo-longitude')}
                size="small"
                fullWidth
                variant="outlined"
                type="number"
                slotProps={{ htmlInput: { min: MIN_LONGITUDE, max: MAX_LONGITUDE, step: 'any' } }}
                value={longitude ?? ''}
                error={!!longitudeError}
                helperText={longitudeError}
                onChange={(e) => {
                  setLongitude(e.target.value === '' ? null : Number(e.target.value));
                  if (longitudeError) setLongitudeError('');
                }}
              />
            </div>

            <SectionLabel>Units</SectionLabel>

            <div className="grid grid-cols-2 gap-3">
              <FormControl size="small" fullWidth variant="outlined">
                <InputLabel>{t('f-temperature-unit')}</InputLabel>
                <Select
                  label={t('f-temperature-unit')}
                  value={temperatureUnit ?? ''}
                  onChange={(e) => setTemperatureUnit(e.target.value as HealthSystemDALEnumsTemperatureUnit)}
                >
                  {getTemperatureUnitOptions(t).map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" fullWidth variant="outlined">
                <InputLabel>{t('f-weight-unit')}</InputLabel>
                <Select
                  label={t('f-weight-unit')}
                  value={weightUnit ?? ''}
                  onChange={(e) => setWeightUnit(e.target.value as HealthSystemDALEnumsWeightType)}
                >
                  {getWeightUnitOptions(t).map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <SectionLabel>Regional</SectionLabel>

            {hasFeature(FeatureType.USE_CULTURE_NAMES) && (
              <CultureSelect fullWidth value={cultureName} onCultureChange={(c) => setCultureName(c)} />
            )}

            <div>
              {mode === 'update' ? (
                <>
                  {!isEditingTimezone ? (
                    <div className="flex items-center gap-1">
                      <TextField
                        label={t('f-settings-opt-timezone-label')}
                        size="small"
                        fullWidth
                        variant="outlined"
                        value={timezoneId ? formatTimezoneLabel(timezoneId) : '—'}
                        slotProps={{ input: { readOnly: true } }}
                      />
                      <Tooltip arrow title={t('f-com-edit')}>
                        <IconButton size="small" onClick={() => setIsEditingTimezone(true)} sx={V4_ICON_BUTTON_SMALL}>
                          <Icon fontSize="small">edit</Icon>
                        </IconButton>
                      </Tooltip>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Autocomplete
                        className="flex-1"
                        options={IANA_TIMEZONES}
                        value={timezoneId || null}
                        onChange={(_: React.SyntheticEvent, value: string | null) => setTimezoneId(value ?? '')}
                        getOptionLabel={(tz) => formatTimezoneLabel(tz)}
                        renderInput={(params) => (
                          <AutocompleteInput
                            params={params}
                            label={t('f-settings-opt-timezone-label')}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      />
                      <Tooltip arrow title={t('f-com-cancel')}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setTimezoneId(farm?.timezoneId ?? '');
                            setIsEditingTimezone(false);
                          }}
                          sx={V4_ICON_BUTTON_SMALL}
                        >
                          <Icon fontSize="small">close</Icon>
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                </>
              ) : (
                <Autocomplete
                  options={IANA_TIMEZONES}
                  value={timezoneId || null}
                  onChange={(_: React.SyntheticEvent, value: string | null) => setTimezoneId(value ?? '')}
                  getOptionLabel={(tz) => formatTimezoneLabel(tz)}
                  renderInput={(params) => (
                    <AutocompleteInput
                      params={params}
                      label={t('f-settings-opt-timezone-label')}
                      size="small"
                      variant="outlined"
                    />
                  )}
                />
              )}
              {tzChanged && <p className="text-xs text-amber-600 mt-1">{t('f-settings-tz-warn-body')}</p>}
            </div>

            <SectionLabel>Classification</SectionLabel>

            <FormControl size="small" fullWidth variant="outlined">
              <InputLabel>{t('f-farm-type')}</InputLabel>
              <Select
                label={t('f-farm-type')}
                value={farmType}
                onChange={(e) => setFarmType(e.target.value as HealthSystemDALEnumsFarmType)}
              >
                <MenuItem value={0} disabled>
                  {t('f-farm-type-select')}
                </MenuItem>
                <MenuItem value={10}>{t('SowGiltPigletFattening')}</MenuItem>
                <MenuItem value={20}>{t('SowPigletGilts')}</MenuItem>
                <MenuItem value={30}>{t('SowPiglet')}</MenuItem>
                <MenuItem value={40}>{t('Fattening')}</MenuItem>
                <MenuItem value={50}>{t('Goat')}</MenuItem>
                <MenuItem value={60}>{t('Cow')}</MenuItem>
                <MenuItem value={70}>{t('Calf')}</MenuItem>
              </Select>
            </FormControl>

            {mode === 'create' && (
              <>
                <SectionLabel>Users</SectionLabel>

                <div className="flex justify-center">
                  <Button
                    size="small"
                    color="primary"
                    variant="outlined"
                    startIcon={<Icon>person_add</Icon>}
                    onClick={() => setCreateUserDialogOpen(true)}
                  >
                    {t('f-farm-add-user')}
                  </Button>
                </div>

                {users.length > 0 && (
                  <List disablePadding>
                    {users.map((user, index) => (
                      <ListItem key={`usr-${index}`} disableGutters>
                        <FarmUserCard
                          user={user}
                          action={
                            <Tooltip arrow title={t('f-com-remove')}>
                              <IconButton
                                color="primary"
                                size="small"
                                onClick={() => handleRemoveUser(index)}
                                sx={V4_ICON_BUTTON_SMALL}
                              >
                                <Icon fontSize="small">delete_outline</Icon>
                              </IconButton>
                            </Tooltip>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </>
            )}
          </div>
        </DialogContent>

        <DialogActions>
          {isBusy && <CircularProgress size={16} />}
          <Button
            onClick={closeDialog}
            variant="outlined"
            disabled={isBusy}
            startIcon={<Icon>close</Icon>}
            color="primary"
          >
            {t('f-com-cancel')}
          </Button>
          <Button
            onClick={handleSaveClick}
            id="createFarmBtn"
            disabled={isBusy}
            startIcon={<Icon>save</Icon>}
            variant="contained"
            color="primary"
          >
            {t('f-com-save')}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateUpdateUserModal
        key={'add-new'}
        opened={createUserDialogOpen}
        returnObject
        onModalAction={handleAddUser}
        user={EMPTY_FARM_USER}
      />
    </>
  );
};

export default CreateUpdateFarmModal;
